export type LabelData = {
  codice: string
  modello: string
  prezzo: number
  prezzo_scontato: number
}

export function createLabel(payload: unknown): { data: LabelData; preview: string; commands: Uint8Array } {
  if (!payload || typeof payload !== 'object') throw new Error('Dati etichetta mancanti')
  const values = payload as Record<string, unknown>
  const price = (value: unknown) => {
    if ((typeof value !== 'number' && typeof value !== 'string') || String(value).trim() === '') {
      throw new Error('Prezzi etichetta mancanti o non validi')
    }
    const parsed = Number(typeof value === 'string' ? value.replace(',', '.') : value)
    if (!Number.isFinite(parsed) || parsed < 0) throw new Error('Prezzi etichetta non validi')
    return parsed
  }
  const data: LabelData = {
    codice: String(values.codice ?? '').trim(),
    modello: String(values.modello ?? '').trim(),
    prezzo: price(values.prezzo),
    prezzo_scontato: price(values.prezzo_scontato),
  }
  if (!data.codice || !data.modello || data.prezzo <= 0 || data.prezzo_scontato > data.prezzo) {
    throw new Error('Dati etichetta non validi')
  }

  // Il riferimento misura 30 x 50 mm. Il PNG mantiene la stessa proporzione.
  const canvas = document.createElement('canvas')
  canvas.width = 720
  canvas.height = 1200
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Impossibile generare la etichetta')
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.fillStyle = '#000'
  ctx.strokeStyle = '#000'
  ctx.lineWidth = 5
  ctx.strokeRect(4, 4, 712, 1192)
  ctx.textAlign = 'center'

  const text = (value: string, y: number, size: number, font: string, outline = false) => {
    ctx.font = font.replace('SIZE', String(size))
    while (ctx.measureText(value).width > 620 && size > 16) {
      size -= 1
      ctx.font = font.replace('SIZE', String(size))
    }
    if (outline) {
      ctx.lineWidth = 4
      ctx.strokeText(value, 360, y)
    } else ctx.fillText(value, 360, y)
    return ctx.measureText(value).width
  }
  const money = (value: number) => new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value) + ' €'

  text('SALDI', 210, 185, 'SIZEpx "Times New Roman", serif')
  const discount = Math.round((1 - data.prezzo_scontato / data.prezzo) * 100)
  text('-' + discount + '%', 580, 310, 'SIZEpx "Times New Roman", serif', true)
  const oldWidth = text(money(data.prezzo), 765, 95, '600 SIZEpx Arial, sans-serif')
  ctx.lineWidth = 9
  ctx.beginPath()
  ctx.moveTo(360 - oldWidth / 2 - 12, 775)
  ctx.lineTo(360 + oldWidth / 2 + 12, 700)
  ctx.stroke()
  text(money(data.prezzo_scontato), 955, 185, '600 SIZEpx Arial, sans-serif')
  ctx.lineWidth = 9
  ctx.beginPath()
  ctx.moveTo(55, 1015)
  ctx.lineTo(665, 1015)
  ctx.stroke()
  text(data.modello, 1110, 53, '600 SIZEpx Arial, sans-serif')

  // 203 dpi: 8 punti/mm. Usa i 72 mm stampabili del rotolo da 80 mm, mantenendo le proporzioni.
  const raster = document.createElement('canvas')
  raster.width = 576
  raster.height = 960
  const rasterCtx = raster.getContext('2d')
  if (!rasterCtx) throw new Error('Impossibile elaborare la etichetta')
  rasterCtx.drawImage(canvas, 0, 0, raster.width, raster.height)
  const pixels = rasterCtx.getImageData(0, 0, raster.width, raster.height)
  for (let i = 0; i < pixels.data.length; i += 4) {
    const black = pixels.data[i] * 0.299 + pixels.data[i + 1] * 0.587 + pixels.data[i + 2] * 0.114 < 180
    pixels.data[i] = pixels.data[i + 1] = pixels.data[i + 2] = black ? 0 : 255
    pixels.data[i + 3] = 255
  }
  rasterCtx.putImageData(pixels, 0, 0)
  return {
    data,
    preview: raster.toDataURL('image/png'),
    commands: encodeRaster(pixels.data, raster.width, raster.height),
  }
}

// GS v 0, bande da 24 righe per limitare il buffer delle stampanti portatili.
export function encodeRaster(rgba: Uint8ClampedArray, width: number, height: number): Uint8Array {
  if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || width > 576 || height < 1 || rgba.length !== width * height * 4) {
    throw new Error('Dimensioni raster non valide')
  }
  const rowBytes = Math.ceil(width / 8)
  const bytes: number[] = [0x1b, 0x40, 0x1b, 0x61, 0, 0x1d, 0x4c, 0, 0]
  for (let top = 0; top < height; top += 24) {
    const rows = Math.min(24, height - top)
    bytes.push(0x1d, 0x76, 0x30, 0, rowBytes & 255, rowBytes >> 8, rows & 255, rows >> 8)
    for (let y = top; y < top + rows; y++) {
      for (let xByte = 0; xByte < rowBytes; xByte++) {
        let packed = 0
        for (let bit = 0; bit < 8; bit++) {
          const x = xByte * 8 + bit
          const i = (y * width + x) * 4
          if (x < width && rgba[i + 3] > 0 && rgba[i] < 180) packed |= 0x80 >> bit
        }
        bytes.push(packed)
      }
    }
  }
  // Avanzamento finale di 20 mm per portare la etichetta oltre il bordo di strappo.
  bytes.push(0x1b, 0x4a, 160)
  return Uint8Array.from(bytes)
}

